import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PageTitle } from '@/components/ui/PageTitle';
import {
	Button,
	SearchInput,
	Select,
	SelectTrigger,
	SelectContent,
	SelectItem,
	SelectValue,
	SlidingTabs,
} from '@/components/ui';
import { FormCategoriesTab } from './FormCategoriesTab';
import {
	useGetForm,
	useGetFormApplicationPeriods,
	useGetForms,
} from '@/services/queries/form';
import { useGetCompanies } from '@/services/queries/company';
import { DataTablePagination } from '@/components/data-table/Pagination';
import {
	CalendarDays,
	FileText,
	Pencil,
	Settings,
	X,
	Check,
	Loader2,
	Trash2,
	AlertTriangle,
} from 'lucide-react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';
import { getErrorMessage } from '@/utils/errorMessage';
import { useRemoveForm } from '@/services/mutations/form';
import { FormService } from '@/services/form.service';

const formatDate = (dateString: string) => {
	const date = new Date(dateString);
	return date.toLocaleDateString('pt-BR');
};

const getFormApplicationModeLabel = (mode: Forms.FormApplicationMode) =>
	mode === 'PER_APPLICATION_PERIOD'
		? 'Re-aplicação por período'
		: 'Resposta única';

const getFormTypeLabel = (type: Forms.FormType): string => {
	if (type === 'ONBOARDING') return 'Onboarding (usuários)';
	if (type === 'EMPLOYEE_NR1') return 'Pesquisa colaboradores (NR-1)';
	if (type === 'CBI') return 'CBI (Burnout)';
	if (type === 'PET') return 'PET (Psicoterapia Breve)';
	if (type === 'MATCH') return 'Match (/match)';
	return type;
};

const getFormTypeBadgeClass = (type: Forms.FormType): string => {
	if (type === 'ONBOARDING') return 'bg-violet-50 text-violet-600';
	if (type === 'EMPLOYEE_NR1') return 'bg-sky-50 text-sky-600';
	if (type === 'CBI') return 'bg-amber-50 text-amber-700';
	if (type === 'PET') return 'bg-emerald-50 text-emerald-700';
	if (type === 'MATCH') return 'bg-pink-50 text-pink-600';
	return 'bg-stone-100 text-stone-600';
};

const getCompanyDisplayName = (company: {
	trade_name?: string | null;
	legal_name?: string | null;
}) =>
	company.trade_name?.trim() ||
	company.legal_name?.trim() ||
	'Empresa sem nome';

const toIsoFromLocalDateTime = (value: string) => new Date(value).toISOString();

const toLocalDateTimeInputValue = (isoDate: string) => {
	const date = new Date(isoDate);
	const timezoneOffset = date.getTimezoneOffset() * 60000;
	return new Date(date.getTime() - timezoneOffset).toISOString().slice(0, 16);
};

interface FormListItemProps {
	form: Forms.PaginatedAdminForms['data'][number];
	onEnter: (id: string) => void;
	onEdit: (form: Forms.PaginatedAdminForms['data'][number]) => void;
	onDelete: (form: Forms.PaginatedAdminForms['data'][number]) => void;
}

const FormListItem = ({
	form,
	onEnter,
	onEdit,
	onDelete,
}: FormListItemProps) => {
	const typeLabel = getFormTypeLabel(form.type);

	return (
		<div className="group flex flex-col gap-3 rounded-2xl border border-stone-100 bg-white p-4 shadow-sm transition-all duration-200 hover:border-stone-200 sm:flex-row sm:items-center sm:justify-between">
			<div className="flex items-start gap-4">
				<div className="flex h-12 w-12 items-center justify-center rounded-xl bg-stone-900 text-white">
					<FileText className="h-6 w-6" />
				</div>
				<div className="flex min-w-0 flex-col gap-1">
					<h3 className="line-clamp-2 text-base font-bold leading-tight text-stone-900">
						{form.title || 'Formulário sem título'}
					</h3>
					<div className="flex flex-wrap items-center gap-2 text-[10px] font-black uppercase tracking-[0.12em] text-stone-400">
						<span
							className={`inline-flex items-center rounded-full px-2 py-0.5 text-[9px] font-black tracking-widest ${getFormTypeBadgeClass(
								form.type,
							)}`}
						>
							{typeLabel}
						</span>
						<span className="text-stone-200">•</span>
						<span>
							{getFormApplicationModeLabel(form.application_mode)}
						</span>
						<span className="text-stone-200">•</span>
						<span>{form.questions_count} perguntas</span>
						<span className="text-stone-200">•</span>
						<span>{form.answered} respostas</span>
						{form.pending > 0 && (
							<>
								<span className="text-stone-200">•</span>
								<span className="text-amber-500">
									{form.pending} pendentes
								</span>
							</>
						)}
					</div>
				</div>
			</div>

			<div className="flex w-full items-center justify-between gap-3 border-t border-stone-100 pt-3 text-xs sm:w-auto sm:border-l sm:border-t-0 sm:pl-4 sm:pt-0">
				<div className="flex flex-col gap-1 text-[10px] font-black uppercase tracking-[0.12em] text-stone-400">
					<span
						className={`inline-flex w-fit items-center rounded-full px-2 py-0.5 text-[9px] font-black tracking-widest ${
							form.active
								? 'bg-emerald-50 text-emerald-600'
								: 'bg-rose-50 text-rose-500'
						}`}
					>
						{form.active ? 'Ativo' : 'Inativo'}
					</span>
					<span className="mt-1 flex items-center gap-1 text-[9px] font-medium text-stone-400">
						<CalendarDays className="h-3 w-3" />
						Criado em {formatDate(form.created_at)}
					</span>
				</div>

				<div className="flex gap-1">
					<button
						type="button"
						onClick={() => onDelete(form)}
						className="flex items-center gap-1 rounded-full p-2 text-rose-400 transition-colors hover:bg-rose-50 hover:text-rose-600"
					>
						<Trash2 size={16} />
						<span className="sm:hidden">Excluir</span>
					</button>
					<button
						type="button"
						onClick={() => onEdit(form)}
						className="flex items-center gap-1 rounded-full p-2 text-stone-400 transition-colors hover:bg-stone-50 hover:text-stone-900"
					>
						<Pencil size={16} />
						<span className="sm:hidden">Editar</span>
					</button>
					<button
						type="button"
						onClick={() => onEnter(form.id)}
						className="flex items-center gap-1 rounded-full p-2 text-stone-400 transition-colors hover:bg-stone-50 hover:text-stone-900"
					>
						<span className="sm:hidden">Configurar</span>
						<Settings size={16} />
					</button>
				</div>
			</div>
		</div>
	);
};

interface CreateFormModalProps {
	isOpen: boolean;
	onClose: () => void;
	onCreated: (form: Forms.Form) => void;
}

const CreateFormModal = ({
	isOpen,
	onClose,
	onCreated,
}: CreateFormModalProps) => {
	const queryClient = useQueryClient();
	const [title, setTitle] = useState('');
	const [type, setType] = useState<Forms.FormType>('ONBOARDING');
	const [applicationMode, setApplicationMode] =
		useState<Forms.FormApplicationMode>('LIFETIME_SINGLE');
	const [companyId, setCompanyId] = useState('');
	const [startsAt, setStartsAt] = useState('');
	const [endsAt, setEndsAt] = useState('');
	const [isCreatingForm, setIsCreatingForm] = useState(false);
	const [isSavingPeriodData, setIsSavingPeriodData] = useState(false);

	const { data: companiesResponse } = useGetCompanies(undefined, {
		enabled: isOpen && applicationMode === 'PER_APPLICATION_PERIOD',
	});
	const companies = companiesResponse?.data ?? [];

	if (!isOpen) return null;

	const isPending = isCreatingForm || isSavingPeriodData;

	const resetState = () => {
		setTitle('');
		setType('ONBOARDING');
		setApplicationMode('LIFETIME_SINGLE');
		setCompanyId('');
		setStartsAt('');
		setEndsAt('');
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (applicationMode === 'PER_APPLICATION_PERIOD') {
			if (!companyId || !startsAt || !endsAt) {
				toast.error(
					'Para re-aplicação por período, selecione a empresa e preencha início/fim.',
				);
				return;
			}
		}

		try {
			setIsCreatingForm(true);
			setIsSavingPeriodData(true);
			const createdForm = await FormService.create({
				title: title || undefined,
				type,
				active: true,
				application_mode: applicationMode,
			});
			queryClient.invalidateQueries({ queryKey: ['forms'] });
			toast.success('Formulário criado com sucesso!');

			if (applicationMode === 'PER_APPLICATION_PERIOD') {
				await FormService.addAudience(createdForm.id, {
					entity_type: 'EMPLOYEE',
					company_id: companyId,
				});
				await FormService.createApplicationPeriod(
					createdForm.id,
					companyId,
					{
						starts_at: toIsoFromLocalDateTime(startsAt),
						ends_at: toIsoFromLocalDateTime(endsAt),
					},
				);
			}

			onCreated(createdForm);
			resetState();
		} catch (error: unknown) {
			toast.error(
				getErrorMessage(
					error,
					'Erro ao criar formulário com período de aplicação',
				),
			);
		} finally {
			setIsCreatingForm(false);
			setIsSavingPeriodData(false);
		}
	};

	const labelClass =
		'ml-1 text-[10px] font-black uppercase tracking-widest text-stone-300';
	const inputClass =
		'w-full rounded-xl border border-stone-200 bg-stone-50/50 px-4 py-3 text-sm font-bold text-stone-700 outline-none focus:border-stone-400 focus:bg-white';

	return createPortal(
		<div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
			<div
				className="absolute inset-0 bg-stone-900/60 backdrop-blur-[2px]"
				onClick={onClose}
			/>
			<div className="animate-in zoom-in-95 relative flex w-full max-w-[520px] flex-col rounded-[2rem] bg-white shadow-2xl duration-200">
				<form onSubmit={handleSubmit} className="flex flex-col">
					<div className="flex shrink-0 items-center justify-between border-b border-stone-50 px-8 py-6">
						<h2 className="text-lg font-black text-stone-900">
							Novo formulário
						</h2>
						<button
							type="button"
							onClick={onClose}
							className="text-stone-400 transition-colors hover:text-stone-600"
						>
							<X size={20} />
						</button>
					</div>

					<div className="flex-1 space-y-6 p-8">
						<div className="space-y-2">
							<label className={labelClass}>
								Título do formulário
							</label>
							<input
								type="text"
								value={title}
								onChange={(e) => setTitle(e.target.value)}
								className={inputClass}
								placeholder="Ex: Onboarding Plumaa"
							/>
						</div>

						<div className="space-y-2">
							<label className={labelClass}>
								Tipo de formulário
							</label>
							<Select
								value={type}
								onValueChange={(value) =>
									setType(value as Forms.FormType)
								}
							>
								<SelectTrigger className={inputClass}>
									<SelectValue>
										{getFormTypeLabel(type)}
									</SelectValue>
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="ONBOARDING">
										Onboarding (usuários)
									</SelectItem>
									<SelectItem value="EMPLOYEE_NR1">
										Pesquisa colaboradores (NR-1)
									</SelectItem>
									<SelectItem value="CBI">
										CBI (Burnout)
									</SelectItem>
									<SelectItem value="PET">
										PET (Psicoterapia Breve)
									</SelectItem>
								</SelectContent>
							</Select>
						</div>

						<div className="space-y-2">
							<label className={labelClass}>
								Modo de aplicação
							</label>
							<Select
								value={applicationMode}
								onValueChange={(value) =>
									setApplicationMode(
										value as Forms.FormApplicationMode,
									)
								}
							>
								<SelectTrigger className={inputClass}>
									<SelectValue>
										{getFormApplicationModeLabel(
											applicationMode,
										)}
									</SelectValue>
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="LIFETIME_SINGLE">
										Resposta única (padrão)
									</SelectItem>
									<SelectItem value="PER_APPLICATION_PERIOD">
										Re-aplicação por período
									</SelectItem>
								</SelectContent>
							</Select>
						</div>

						{applicationMode === 'PER_APPLICATION_PERIOD' && (
							<>
								<div className="space-y-2">
									<label className={labelClass}>
										Empresa do período
									</label>
									<Select
										value={companyId}
										onValueChange={setCompanyId}
									>
										<SelectTrigger className={inputClass}>
											<SelectValue>
												{companyId
													? getCompanyDisplayName(
															companies.find(
																(c) =>
																	c.id ===
																	companyId,
															) ?? {},
													  )
													: 'Selecione a empresa'}
											</SelectValue>
										</SelectTrigger>
										<SelectContent>
											{companies.map((company) => (
												<SelectItem
													key={company.id}
													value={company.id}
												>
													{getCompanyDisplayName(
														company,
													)}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>

								<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
									<div className="space-y-2">
										<label className={labelClass}>
											Início do período
										</label>
										<input
											type="datetime-local"
											value={startsAt}
											onChange={(e) =>
												setStartsAt(e.target.value)
											}
											className={inputClass}
										/>
									</div>
									<div className="space-y-2">
										<label className={labelClass}>
											Fim do período
										</label>
										<input
											type="datetime-local"
											value={endsAt}
											onChange={(e) =>
												setEndsAt(e.target.value)
											}
											className={inputClass}
										/>
									</div>
								</div>
							</>
						)}
					</div>

					<div className="flex shrink-0 items-center justify-end gap-6 border-t border-stone-50 p-8">
						<button
							type="button"
							onClick={onClose}
							disabled={isPending}
							className="text-sm font-bold text-stone-400 hover:text-stone-600 disabled:opacity-50"
						>
							Cancelar
						</button>
						<Button
							type="submit"
							text={isPending ? 'Criando...' : 'Criar formulário'}
							disabled={isPending}
							className="min-w-[160px] rounded-full bg-black px-8 py-2.5 text-sm font-bold text-white shadow-lg hover:bg-neutral-800"
							startIcon={
								isPending ? (
									<Loader2 className="size-4 animate-spin" />
								) : (
									<Check className="size-4 stroke-[3]" />
								)
							}
						/>
					</div>
				</form>
			</div>
		</div>,
		document.body,
	);
};

interface EditFormModalProps {
	form: FormListItemProps['form'] | null;
	onClose: () => void;
}

const EditFormModal = ({ form, onClose }: EditFormModalProps) => {
	const queryClient = useQueryClient();
	const formId = form?.id ?? '';
	const [title, setTitle] = useState(form?.title ?? '');
	const [type, setType] = useState<Forms.FormType>(
		form?.type ?? 'ONBOARDING',
	);
	const [applicationMode, setApplicationMode] =
		useState<Forms.FormApplicationMode>(
			form?.application_mode ?? 'LIFETIME_SINGLE',
		);
	const [companyId, setCompanyId] = useState('');
	const [startsAt, setStartsAt] = useState('');
	const [endsAt, setEndsAt] = useState('');
	const [editingPeriodId, setEditingPeriodId] = useState<string | null>(null);
	const [isPatchingForm, setIsPatchingForm] = useState(false);
	const [isSavingPeriodData, setIsSavingPeriodData] = useState(false);

	const { data: formDetail } = useGetForm(formId, {
		enabled: !!formId,
	});
	const { data: companiesResponse } = useGetCompanies(undefined, {
		enabled: !!formId && applicationMode === 'PER_APPLICATION_PERIOD',
	});
	const companies = companiesResponse?.data ?? [];
	const linkedCompanies =
		formDetail?.form_audience?.filter(
			(audience) => audience.entity_type === 'EMPLOYEE',
		) ?? [];

	const { data: periodsResponse, isFetching: isLoadingPeriods } =
		useGetFormApplicationPeriods(
			formId,
			companyId,
			{ page: 1, limit: 100 },
			{
				enabled:
					!!formId &&
					applicationMode === 'PER_APPLICATION_PERIOD' &&
					!!companyId,
			},
		);
	const periods = periodsResponse?.data ?? [];

	useEffect(() => {
		if (form) {
			setTitle(form.title ?? '');
			setType(form.type ?? 'ONBOARDING');
			setApplicationMode(form.application_mode ?? 'LIFETIME_SINGLE');
			setEditingPeriodId(null);
			setStartsAt('');
			setEndsAt('');
		}
	}, [form?.id, form?.title, form?.type, form?.application_mode]);

	useEffect(() => {
		if (applicationMode !== 'PER_APPLICATION_PERIOD') {
			setCompanyId('');
			setEditingPeriodId(null);
			setStartsAt('');
			setEndsAt('');
			return;
		}
		if (!companyId && linkedCompanies.length > 0) {
			const firstCompanyId = linkedCompanies[0].company_id;
			if (firstCompanyId) setCompanyId(firstCompanyId);
		}
	}, [applicationMode, linkedCompanies, companyId]);

	if (!form) return null;

	const isPending = isPatchingForm || isSavingPeriodData;

	const handleEditPeriod = (period: Forms.FormApplicationPeriod) => {
		setEditingPeriodId(period.id);
		setStartsAt(toLocalDateTimeInputValue(period.starts_at));
		setEndsAt(toLocalDateTimeInputValue(period.ends_at));
	};

	const clearPeriodDraft = () => {
		setEditingPeriodId(null);
		setStartsAt('');
		setEndsAt('');
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		const body: Forms.UpdateForm = {};
		const nextTitle = title.trim() || undefined;
		const prevTitle = (form.title ?? '').trim() || undefined;
		if (nextTitle !== prevTitle) body.title = nextTitle;

		if (type !== form.type) body.type = type;
		if (applicationMode !== form.application_mode) {
			body.application_mode = applicationMode;
		}

		const periodFieldsCount = [companyId, startsAt, endsAt].filter(
			Boolean,
		).length;
		if (
			applicationMode === 'PER_APPLICATION_PERIOD' &&
			periodFieldsCount > 0 &&
			periodFieldsCount < 3
		) {
			toast.error(
				'Preencha empresa, início e fim para salvar o período.',
			);
			return;
		}

		const hasPeriodDraft =
			applicationMode === 'PER_APPLICATION_PERIOD' &&
			!!companyId &&
			!!startsAt &&
			!!endsAt;

		if (Object.keys(body).length === 0 && !hasPeriodDraft) {
			onClose();
			return;
		}

		try {
			setIsSavingPeriodData(true);

			if (Object.keys(body).length > 0) {
				setIsPatchingForm(true);
				await FormService.patch(form.id, body);
				queryClient.invalidateQueries({ queryKey: ['forms'] });
				queryClient.invalidateQueries({ queryKey: ['form', form.id] });
				toast.success('Formulário atualizado com sucesso!');
			}

			if (hasPeriodDraft) {
				const periodPayload: Forms.CreateFormApplicationPeriod = {
					starts_at: toIsoFromLocalDateTime(startsAt),
					ends_at: toIsoFromLocalDateTime(endsAt),
				};

				const isLinkedToForm = linkedCompanies.some(
					(audience) => audience.company_id === companyId,
				);

				if (!isLinkedToForm) {
					await FormService.addAudience(form.id, {
						entity_type: 'EMPLOYEE',
						company_id: companyId,
					});
				}

				if (editingPeriodId) {
					await FormService.patchApplicationPeriod(
						editingPeriodId,
						periodPayload,
					);
				} else {
					await FormService.createApplicationPeriod(
						form.id,
						companyId,
						periodPayload,
					);
				}

				toast.success('Período de aplicação salvo com sucesso!');
				queryClient.invalidateQueries({ queryKey: ['forms'] });
				queryClient.invalidateQueries({ queryKey: ['form', form.id] });
			}

			onClose();
		} catch (error: unknown) {
			toast.error(
				getErrorMessage(
					error,
					'Erro ao salvar formulário/período de aplicação',
				),
			);
		} finally {
			setIsPatchingForm(false);
			setIsSavingPeriodData(false);
		}
	};

	const labelClass =
		'ml-1 text-[10px] font-black uppercase tracking-widest text-stone-300';
	const inputClass =
		'w-full rounded-xl border border-stone-200 bg-stone-50/50 px-4 py-3 text-sm font-bold text-stone-700 outline-none focus:border-stone-400 focus:bg-white';

	return createPortal(
		<div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
			<div
				className="absolute inset-0 bg-stone-900/60 backdrop-blur-[2px]"
				onClick={onClose}
			/>
			<div className="animate-in zoom-in-95 relative flex w-full max-w-[520px] flex-col rounded-[2rem] bg-white shadow-2xl duration-200">
				<form onSubmit={handleSubmit} className="flex flex-col">
					<div className="flex shrink-0 items-center justify-between border-b border-stone-50 px-8 py-6">
						<h2 className="text-lg font-black text-stone-900">
							Editar formulário
						</h2>
						<button
							type="button"
							onClick={onClose}
							className="text-stone-400 transition-colors hover:text-stone-600"
						>
							<X size={20} />
						</button>
					</div>

					<div className="flex-1 space-y-6 p-8">
						<div className="space-y-2">
							<label className={labelClass}>
								Título do formulário
							</label>
							<input
								type="text"
								value={title}
								onChange={(e) => setTitle(e.target.value)}
								className={inputClass}
								placeholder="Ex: Onboarding Plumaa"
							/>
						</div>

						<div className="space-y-2">
							<label className={labelClass}>
								Tipo de formulário
							</label>
							<Select
								value={type}
								onValueChange={(value) =>
									setType(value as Forms.FormType)
								}
							>
								<SelectTrigger className={inputClass}>
									<SelectValue>
										{getFormTypeLabel(type)}
									</SelectValue>
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="ONBOARDING">
										Onboarding (usuários)
									</SelectItem>
									<SelectItem value="EMPLOYEE_NR1">
										Pesquisa colaboradores (NR-1)
									</SelectItem>
									<SelectItem value="CBI">
										CBI (Burnout)
									</SelectItem>
									<SelectItem value="PET">
										PET (Psicoterapia Breve)
									</SelectItem>
								</SelectContent>
							</Select>
						</div>

						<div className="space-y-2">
							<label className={labelClass}>
								Modo de aplicação
							</label>
							<Select
								value={applicationMode}
								onValueChange={(value) =>
									setApplicationMode(
										value as Forms.FormApplicationMode,
									)
								}
							>
								<SelectTrigger className={inputClass}>
									<SelectValue>
										{getFormApplicationModeLabel(
											applicationMode,
										)}
									</SelectValue>
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="LIFETIME_SINGLE">
										Resposta única (padrão)
									</SelectItem>
									<SelectItem value="PER_APPLICATION_PERIOD">
										Re-aplicação por período
									</SelectItem>
								</SelectContent>
							</Select>
						</div>

						{applicationMode === 'PER_APPLICATION_PERIOD' && (
							<div className="space-y-4 rounded-xl border border-stone-100 bg-stone-50/40 p-4">
								<div className="space-y-2">
									<label className={labelClass}>
										Empresa do período
									</label>
									<Select
										value={companyId}
										onValueChange={setCompanyId}
									>
										<SelectTrigger className={inputClass}>
											<SelectValue>
												{companyId
													? getCompanyDisplayName(
															companies.find(
																(company) =>
																	company.id ===
																	companyId,
															) ?? {},
													  )
													: 'Selecione a empresa'}
											</SelectValue>
										</SelectTrigger>
										<SelectContent>
											{companies.map((company) => (
												<SelectItem
													key={company.id}
													value={company.id}
												>
													{getCompanyDisplayName(
														company,
													)}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>

								<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
									<div className="space-y-2">
										<label className={labelClass}>
											Início do período
										</label>
										<input
											type="datetime-local"
											value={startsAt}
											onChange={(e) =>
												setStartsAt(e.target.value)
											}
											className={inputClass}
										/>
									</div>
									<div className="space-y-2">
										<label className={labelClass}>
											Fim do período
										</label>
										<input
											type="datetime-local"
											value={endsAt}
											onChange={(e) =>
												setEndsAt(e.target.value)
											}
											className={inputClass}
										/>
									</div>
								</div>

								<div className="flex items-center justify-between">
									<p className="text-xs font-black uppercase tracking-[0.15em] text-stone-400">
										{editingPeriodId
											? 'Editando período existente'
											: 'Novo período'}
									</p>
									{(startsAt ||
										endsAt ||
										editingPeriodId) && (
										<button
											type="button"
											onClick={clearPeriodDraft}
											className="text-xs font-bold text-stone-500 hover:text-stone-700"
										>
											Limpar período
										</button>
									)}
								</div>

								<div className="space-y-2">
									<p className="text-xs font-black uppercase tracking-[0.15em] text-stone-400">
										Períodos existentes
									</p>
									{!companyId ? (
										<p className="text-sm text-stone-400">
											Selecione uma empresa para listar os
											períodos.
										</p>
									) : isLoadingPeriods ? (
										<p className="text-sm text-stone-400">
											Carregando períodos...
										</p>
									) : periods.length === 0 ? (
										<p className="text-sm text-stone-400">
											Nenhum período cadastrado para esta
											empresa.
										</p>
									) : (
										<div className="space-y-2">
											{periods.map((period) => (
												<button
													key={period.id}
													type="button"
													onClick={() =>
														handleEditPeriod(period)
													}
													className="flex w-full items-center justify-between rounded-lg border border-stone-200 bg-white px-3 py-2 text-left hover:border-stone-300"
												>
													<span className="text-xs font-semibold text-stone-700">
														{formatDate(
															period.starts_at,
														)}{' '}
														-{' '}
														{formatDate(
															period.ends_at,
														)}
													</span>
													<span className="text-[10px] font-black uppercase tracking-widest text-stone-500">
														Editar
													</span>
												</button>
											))}
										</div>
									)}
								</div>
							</div>
						)}
					</div>

					<div className="flex shrink-0 items-center justify-end gap-6 border-t border-stone-50 p-8">
						<button
							type="button"
							onClick={onClose}
							disabled={isPending}
							className="text-sm font-bold text-stone-400 hover:text-stone-600 disabled:opacity-50"
						>
							Cancelar
						</button>
						<Button
							type="submit"
							text={
								isPending ? 'Salvando...' : 'Salvar alterações'
							}
							disabled={isPending}
							className="min-w-[160px] rounded-full bg-black px-8 py-2.5 text-sm font-bold text-white shadow-lg hover:bg-neutral-800"
							startIcon={
								isPending ? (
									<Loader2 className="size-4 animate-spin" />
								) : (
									<Check className="size-4 stroke-[3]" />
								)
							}
						/>
					</div>
				</form>
			</div>
		</div>,
		document.body,
	);
};

const DeleteModal = ({
	isOpen,
	onClose,
	onConfirm,
	title,
	description,
}: {
	isOpen: boolean;
	onClose: () => void;
	onConfirm: () => void | Promise<void>;
	title: string;
	description: string;
}) => {
	if (!isOpen) return null;

	return createPortal(
		<div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
			<div
				className="absolute inset-0 bg-stone-900/60 backdrop-blur-[2px]"
				onClick={onClose}
			/>
			<div className="animate-in zoom-in-95 relative w-full max-w-[400px] rounded-[2rem] bg-white p-8 text-center shadow-2xl duration-200">
				<div className="mb-4 flex justify-center text-rose-500">
					<div className="rounded-full bg-rose-50 p-4">
						<AlertTriangle size={32} />
					</div>
				</div>
				<h3 className="mb-2 text-xl font-black text-stone-900">
					{title}
				</h3>
				<p
					className="mb-8 text-sm font-medium leading-relaxed text-stone-500"
					dangerouslySetInnerHTML={{ __html: description }}
				/>
				<div className="flex flex-col gap-3">
					<Button
						text="Confirmar exclusão"
						className="w-full justify-center rounded-full border-none bg-rose-600 py-3 font-bold text-white shadow-md hover:bg-rose-700"
						onClick={onConfirm}
					/>
					<button
						onClick={onClose}
						className="text-sm font-bold text-stone-400 transition-colors hover:text-stone-600"
					>
						Cancelar
					</button>
				</div>
			</div>
		</div>,
		document.body,
	);
};

const Forms = () => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const [page, setPage] = useState(1);
	const [isCreateOpen, setIsCreateOpen] = useState(false);
	const [editingForm, setEditingForm] = useState<
		FormListItemProps['form'] | null
	>(null);
	const [formToDelete, setFormToDelete] = useState<
		FormListItemProps['form'] | null
	>(null);
	const [isDeleteOpen, setIsDeleteOpen] = useState(false);

	const search = searchParams.get('search') || '';

	const { data: formsResponse, isFetching } = useGetForms({
		page,
		limit: 5,
	});

	const list = formsResponse?.data ?? [];
	const pagination = formsResponse?.pagination;

	const { mutateAsync: removeForm } = useRemoveForm(() => {
		setIsDeleteOpen(false);
		setFormToDelete(null);
	});

	const filteredList = list.filter((form) => {
		const normalizedSearch = search.toLowerCase();
		return (
			(form.title || '').toLowerCase().includes(normalizedSearch) ||
			form.type.toLowerCase().includes(normalizedSearch)
		);
	});

	const handlePageChange = (newPage: number) => {
		setPage(newPage);
	};

	const handleDeleteConfirm = async () => {
		if (formToDelete?.id) {
			await removeForm(formToDelete.id);
		}
	};

	const listTabContent = (
		<>
			<div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
				<div className="w-full sm:max-w-md">
					<SearchInput
						placeholder="Buscar por título ou tipo..."
						className="bg-inherit"
					/>
				</div>
				<Button
					text="Novo formulário"
					className="w-full rounded-full bg-black px-6 py-2.5 font-bold shadow-md hover:bg-neutral-800 sm:w-auto"
					onClick={() => setIsCreateOpen(true)}
				/>
			</div>

			<div className="w-full space-y-2">
				{isFetching && (
					<p className="animate-pulse px-2 text-sm font-black uppercase tracking-widest text-stone-400">
						Carregando formulários...
					</p>
				)}

				{!isFetching && filteredList.length > 0 && (
					<>
						{filteredList.map((form) => (
							<FormListItem
								key={form.id}
								form={form}
								onEnter={(id) =>
									navigate(`/admin/formularios/${id}`)
								}
								onEdit={(formToEdit) =>
									setEditingForm(formToEdit)
								}
								onDelete={(formToDeleteRow) => {
									setFormToDelete(formToDeleteRow);
									setIsDeleteOpen(true);
								}}
							/>
						))}
					</>
				)}

				{!isFetching && filteredList.length === 0 && (
					<div className="flex flex-col items-center justify-center rounded-[2rem] border border-dashed border-stone-200 bg-stone-50/50 py-24 text-center">
						<div className="mb-4 rounded-full bg-white p-4 text-stone-300 shadow-sm">
							<FileText className="h-8 w-8" />
						</div>
						<h3 className="text-base font-bold text-stone-900">
							Nenhum formulário encontrado
						</h3>
						<p className="mx-auto mt-1 max-w-xs text-sm leading-relaxed text-stone-400">
							Crie o primeiro formulário para iniciar o onboarding
							ou as pesquisas com colaboradores.
						</p>
						<Button
							text="Criar primeiro formulário"
							className="mt-4 rounded-full bg-black px-6 py-2.5 text-sm font-bold shadow-md hover:bg-neutral-800"
							onClick={() => setIsCreateOpen(true)}
						/>
					</div>
				)}
			</div>

			{pagination && pagination.total > 0 && (
				<div className="flex justify-center pt-4">
					<DataTablePagination
						page={page}
						pageSize={pagination.limit}
						totalItems={pagination.total}
						onPageChange={handlePageChange}
					/>
				</div>
			)}
		</>
	);

	return (
		<section className="animate-fade-in space-y-6 px-4 py-6">
			<div className="space-y-1">
				<PageTitle text="Formulários" />
				<p className="text-sm text-stone-400">
					Gerencie categorias, subcategorias e a listagem de
					formulários.
				</p>
			</div>

			<SlidingTabs
				defaultActiveTab="lista"
				tabs={[
					{
						id: 'lista',
						label: 'Formulários',
						children: (
							<div className="mt-4 space-y-6">
								{listTabContent}
							</div>
						),
					},
					{
						id: 'categorias',
						label: 'Categorias',
						children: <FormCategoriesTab />,
					},
				]}
			/>

			<CreateFormModal
				isOpen={isCreateOpen}
				onClose={() => setIsCreateOpen(false)}
				onCreated={(form) => {
					setIsCreateOpen(false);
					navigate(`/admin/formularios/${form.id}`);
				}}
			/>

			<EditFormModal
				form={editingForm}
				onClose={() => setEditingForm(null)}
			/>

			<DeleteModal
				isOpen={isDeleteOpen}
				onClose={() => setIsDeleteOpen(false)}
				onConfirm={handleDeleteConfirm}
				title="Excluir formulário"
				description={`Você tem certeza que deseja excluir o formulário <strong>${
					formToDelete?.title || 'sem título'
				}</strong>?`}
			/>
		</section>
	);
};

export { Forms };
